/**
 * DetailView Component
 *
 * Description: Component to display a detailed view of an order including order information, items, and history.
 * It now includes an optional `changeHistory` prop that triggers the re-fetching of order history when updated.
 *
 * Props:
 * - item (IOrderDetails): The main order data object to be displayed.
 * - id (string): The unique identifier for the order.
 * - changeHistory (boolean, optional): If true, triggers a re-fetch of the order history when it changes. Default is false.
 *
 * File Name: DetailView.tsx
 * Date: 15-08-24
 */

import React, { useEffect, useState } from "react";
import {
  displayStatus,
  getLastSegment,
  truncateString,
} from "src/helpers/helperFunctions";
import { IAddressDetails, IOrderDetails } from "src/types/GlobalInterfaces";
import edit from "../../assets/images/awesome-edit.svg";
import orderCircleImg from "src/assets/images/circle-icon-order-detail.png";
import moment from "moment";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { printCity } from "src/Config/CityList";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faStar, faPencil } from "@fortawesome/free-solid-svg-icons";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import defaultProfile from "src/assets/images/default_profile_image.png";
import SimpleInput from "src/Components/SimpleInput";
import { capitalize, Modal } from "@mui/material";
import SelectDropdown from "./SelectDropdown";
import {
  CURRENCY,
  STANDARD_DELIVERY_CHARGE,
  EXPRESS_DELIVERY_CHARGE,
} from "src/lib/constans";
import { useQueryParams } from "src/hooks/useQueryParams";
// Interface for order history
interface IHistory {
  _id: string;
  dateAdded: string;
  comment: string;
  status: string;
  [key: string]: any;
}
const customeStyle: React.CSSProperties = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

// Component to display detailed view of an order
const DetailView: React.FC<{
  item: IOrderDetails;
  id: string;
  changeHistory?: boolean;
  isPharma?: boolean;
  isAdmin?: boolean;
}> = ({
  item,
  id,
  changeHistory = false,
  isPharma = false,
  isAdmin = false,
}) => {
  console.log(
    isAdmin,
    "isAdminisAdminisAdminisAdminisAdminisAdminisAdminisAdminisAdminisAdminisAdmin"
  );
  const [history, setHistory] = useState<IHistory[]>([]); // State to store the order history
  const [reviewModal, setReviewModal] = useState(false);
  const [productDetails, setProductDetails] = useState<any>({});
  const [isEdit, setIsEdit] = useState(false);
  const [comment, setComment] = useState("");
  const [addressLoader, setAddressLoader] = useState(false);
  const [star, setStar] = useState(0);
  const [isLoader, setIsLoader] = useState(false);
  const [allAddresses, setAllAddresses] = useState<
    { value: string; label: string }[]
  >([]);
  const [getLoader, setGetLoader] = useState<boolean>(false);
  const [updateAddress, setUpdateAddress] = useState<any>("");

  const searchParams = useQueryParams();
  const type = searchParams.getParam("type") || "";

  const defaultAddressDetail: any = {
    _id: "",
    customerId: "",
    addressType: "",
    landmarks: "",
    personName: "",
    mobileNumber: "",
    streetAddress: "",
    flatNo: "",
    buildingName: "",
    emirate: "",
    city: "",
    country: "",
    createdAt: "",
    updatedAt: "",
  };

  let addressDetail: any = defaultAddressDetail;

  if (item?.addressDetail) {
    try {
      if (isPharma) {
        let d = JSON.parse(item?.addressDetail);
        addressDetail = d;
      } else {
        addressDetail = item?.addressDetail;
      }
    } catch (error) {
      addressDetail = defaultAddressDetail;
    }
  } else {
    addressDetail = defaultAddressDetail;
  }

  const fetchAllAddresses = async () => {
    setGetLoader(true);
    const { res, err } = await httpRequest<any>({
      path: "/customer-address/get-customer-addresss",
    });
    if (res) {
      setAllAddresses(
        res?.data?.map((v: any) => ({
          value: v?.address,
          label: v?.address,
        })) || []
      );
    } else {
      toast(err, { type: "error" });
    }
    setGetLoader(false);
  };
  useEffect(() => {
    fetchAllAddresses();
  }, []);
  useEffect(() => {
    if (item) {
      setUpdateAddress(item?.shippingAddress);
    }
  }, [item]);
  //API_CALL: Fetch order history from the server
  const fetchOrderHistory = async (_id: string) => {
    const path = isPharma
      ? `/pharma-order/history/status/${_id}`
      : `/diagnostic-order/history/status/${_id}`;
    const { res, err } = await httpRequest<IHistory[]>({ path });
    if (res) {
      setHistory(res); // Update state with fetched history
    } else {
      toast(err, { type: "error" }); // Show error toast if the request fails
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrderHistory(id);
    }
  }, [id, changeHistory]); // Fetch order history when the component mounts or the id changes
  const [fileView, setFileView] = useState<null | string>(null);

  const handleOpenModal = (fileUrl: string) => {
    const isPDF = fileUrl.endsWith(".pdf");
    if (isPDF) {
      window.open(`${fileUrl}`, "_blank");
    } else {
      setFileView(fileUrl);
    }
  };

  const handleCloseModal = () => {
    setFileView(null);
  };
  const handleReviewModal = (v: any) => {
    console.log("V", v);
    if (id) {
      setReviewModal(true);
      setProductDetails(v);
    } else {
      toast("Invalid product", { type: "error" });
    }
  };
  const handleCloseReviewModal = () => {
    setReviewModal(false);
    setProductDetails({});
    setStar(0);
    setComment("");
  };
  const backBtnMaain: React.CSSProperties = {
    position: "absolute",
    top: -10,
    right: -10,
    backgroundColor: "red",
    borderRadius: 100,
    width: 20,
    height: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };
  const backBtn: React.CSSProperties = {
    color: "white",
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (star < 1) {
      toast("Please select rating", { type: "error" });
      return;
    }
    if (!comment) {
      toast("Please write a review", { type: "error" });
      return;
    }
    setIsLoader(true);
    const { res, err } = await httpRequest<any>({
      method: "post",
      path: "/review/add?productType=review",
      params: {
        productId: productDetails.productId,
        message: comment,
        rating: star,
      },
    });
    if (res) {
      toast(res?.message, { type: "success" });
      handleCloseReviewModal();
    } else {
      toast(err, { type: "error" });
    }
    setIsLoader(false);
  };
  let Total = item?.totalAmount + (item?.discountedPrice || 0);
  const changeShippingAddress = (value: any) => {
    setUpdateAddress(value);
  };
  const handleAddressUpdate = async () => {
    // e.preventDefault()
    if (!updateAddress) {
      toast("Please select the address", { type: "error" });
      return;
    }
    setAddressLoader(true);

    const { res, err } = await httpRequest<any>({
      method: "put",
      path: `/pharma-order/update-order-address/${item?.orderDetails?.[0]?.orderId}`,
      params: {
        shippingAddress: updateAddress,
      },
    });
    if (res) {
      toast(res?.message, { type: "success" });
    } else {
      toast(err, { type: "error" });
    }
    setAddressLoader(false);
  };
  return (
    <>
      <div style={{ alignItems: "center", display: "flex", gap: "0.5rem" }}>
        <img className="order-detail-img" src={orderCircleImg} alt="" />
        <span className="order-no font-size-20px font-Poppins-Regular">
          {`Order (#${getLastSegment(item?.serialOrderId)})`}
        </span>
      </div>
      <div className="order-detail-main">
        <div className="order-detail-child">
          <div className="order-detail-head">
            <h3 className="font-size-15px font-Poppins-SemiBold">Invoice</h3>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {item?.serialOrderId}
            </h4>
          </div>
        </div>
        <div className="order-detail-child">
          <div className="order-detail-head">
            <h3 className="font-size-15px font-Poppins-SemiBold">Customer</h3>
            <h4
              style={{ textTransform: "capitalize" }}
              className="font-size-15px font-Poppins-Regular order-no"
            >
              {`${item?.name || "-"}`}
            </h4>
          </div>
        </div>
        <div className="order-detail-child">
          <div className="order-detail-head">
            <h3 className="font-size-15px font-Poppins-SemiBold">Date Added</h3>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {moment(item?.orderDate).format("DD-MM-YYYY")}
            </h4>
          </div>
        </div>
      </div>
      {isPharma ? (
        <>
          <div
            style={{
              color: "white",
              gridTemplateColumns:
                item.status === "delivered" && !isAdmin
                  ? "20.66% 16.66% 16.66% 16.66% 12.66% 16.66%"
                  : "20% 20% 20% 20% 20%",
            }}
            className="order-add-table font-size-15px font-Poppins-SemiBold"
          >
            <p>Product Name</p>
            <p>Category</p>
            <p>Quantity</p>
            <p>Unit Price</p>
            <p>Total Price</p>
            {item?.status === "delivered" && !isAdmin && <p>Review</p>}
          </div>
          {item?.orderDetails.map((v, i: number) => (
            <div
              key={i}
              style={{
                border: "1px solid #949494",
                alignItems: "center",
                gridTemplateColumns:
                  item.status === "delivered" && !isAdmin
                    ? "20.66% 16.66% 16.66% 16.66% 12.66% 16.66%"
                    : "20% 20% 20% 20% 20%",
              }}
              className="sub-order-add-table font-size-15px font-Poppins-Regular"
            >
              <p className="Order-product-name-bdl" title={v?.productName}>
                {truncateString(v?.productName || "", 34)}
              </p>
              <p>{v?.mainCategoryName}</p>
              <p>{v?.quantity || "-"}</p>
              <p>{`${CURRENCY} ${v?.productPrice || "-"}`}</p>
              <p>{`${CURRENCY} ${(v?.quantity * v?.productPrice).toFixed(
                2
              )}`}</p>
              {item.status === "delivered" && !isAdmin && (
                <p>
                  <PrimaryBtn
                    onPress={() => handleReviewModal(v)}
                    type="button"
                    ButtonText="Review"
                    btnExtraStyle={{
                      backgroundColor: "#004540",
                      borderRadius: "1.5rem",
                      width: "fit-content",
                      padding: "0.4rem 2rem",
                    }}
                  />
                </p>
              )}
            </div>
          ))}
        </>
      ) : (
        <>
          <div
            style={{ color: "white" }}
            className={`order-add-table font-size-15px font-Poppins-SemiBold ${
              item?.status === "delivered" && !isAdmin
                ? "grid-cols-4"
                : "grid-cols-3"
            } `}
          >
            <p>Product Name</p>
            <p>Test Type</p>
            <p>Price</p>
            {item?.status === "delivered" && !isAdmin && <p>Review</p>}
          </div>
          {item?.orderDetails.map((v, i: number) => (
            <div
              key={i}
              style={{ border: "1px solid #949494", alignItems: "center" }}
              className={`sub-order-add-table font-size-15px font-Poppins-Regular grid-cols-3 ${
                item?.status === "delivered" && !isAdmin
                  ? "grid-cols-4"
                  : "grid-cols-3"
              }`}
            >
              <p className="Order-product-name-bdl" title={v?.productName}>
                {truncateString(v?.productName || "", 34)}
              </p>
              <p>
                {v?.itemType ? capitalize((v?.itemType as string) || "") : "-"}
              </p>
              <p>{v?.productPrice || "-"}</p>
              {item.status === "delivered" && !isAdmin && (
                <p>
                  <PrimaryBtn
                    onPress={() => handleReviewModal(v)}
                    type="button"
                    ButtonText="Review"
                    btnExtraStyle={{
                      backgroundColor: "#004540",
                      borderRadius: "1.5rem",
                      width: "fit-content",
                      padding: "0.4rem 2rem",
                    }}
                  />
                </p>
              )}
            </div>
          ))}
        </>
      )}
      <div className="md:grid-cols-2 gap-y-3 lg:gap-y-[1vw] 2xl:gap-x-10 grid gap-x-7 mt-[2rem] mb-[1rem]">
        <div className={`order-detail-child`}>
          <div className="order-detail-head">
            <h3
              style={{ color: "black" }}
              className="font-size-15px font-Poppins-Medium"
            >
              {/* Payment Address */}
              User Details
            </h3>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {`${item?.name || "-"}`}
            </h4>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {item?.email}
            </h4>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {item?.mobile}
            </h4>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {item?.serialOrderId}
            </h4>
            <h4 className="font-size-15px w-[80%] font-Poppins-Regular order-no">
              {item?.address}
            </h4>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {printCity(item?.city)}
            </h4>
            <h4 className="font-size-15px font-Poppins-Regular order-no">
              {item?.postCode}
            </h4>
          </div>
        </div>
        <div className="order-detail-child">
          <div className="order-detail-head">
            <div className="flex flex-row justify-between items-center">
              <h3
                style={{ color: "black" }}
                className="font-size-15px font-Poppins-Medium"
              >
                Shipping Address
              </h3>
              {item?.status === "pending" && !isAdmin && (
                <>
                  {isEdit ? (
                    <FontAwesomeIcon
                      onClick={() => setIsEdit(false)}
                      icon={faXmark}
                      className="text-gray-600"
                    />
                  ) : (
                    <img
                      className="w-[1vw]"
                      onClick={() => setIsEdit(true)}
                      src={edit}
                      alt=""
                    />
                  )}
                </>
              )}
            </div>
            {isEdit ? (
              <>
                <div className="col-span-2 city-dropdown-option">
                  <SelectDropdown
                    loader={getLoader}
                    isLabel={false}
                    labelText="Delivery Address"
                    options={allAddresses}
                    data={allAddresses.filter(
                      (v) => v?.value === updateAddress
                    )}
                    setData={(e) => changeShippingAddress(e[0]?.value || "")}
                    isMulti={false}
                    // customRendrer={customValueRenderer}
                  />
                </div>
                <div className="flex justify-end">
                  <PrimaryBtn
                    loader={addressLoader}
                    onPress={handleAddressUpdate}
                    ButtonText="Update"
                    btnExtraStyle={{
                      borderRadius: "1.5rem",
                      background: "#00443f",
                      marginTop: "1rem",
                    }}
                  />
                </div>
              </>
            ) : (
              <h4 className="font-size-15px font-Poppins-Regular order-no">
                {`${addressDetail?.personName || ""}`} <br />
                {`${addressDetail?.mobileNumber || ""}`} <br />
                {`${addressDetail?.streetAddress || ""}`} <br />
                {`${addressDetail?.flatNo || ""}`} <br />
                {`${addressDetail?.buildingName || ""}`} <br />
                {`${addressDetail?.emirate || ""}`} <br />
                {`${addressDetail?.city || ""}`} <br />
              </h4>
            )}
          </div>
        </div>
        {isPharma && (
          <div className="w-full flex flex-col gap-1 border border-gray-300 rounded-md 2xl:p-4 p-3 bg-white">
            <p className="font-size-14px text-[#c2c1c1] font-Poppins-Regular">
              Shipping Method
            </p>
            <p className="font-size-16px text-[#8b8a8a] font-Poppins-Regular">
              {item?.deliveryType === "instant delivery"
                ? "Instant Delivery"
                : "Standard Delivery"}
            </p>
          </div>
        )}
        <div className="w-full flex flex-col gap-1 border border-gray-300 rounded-md 2xl:p-4 p-3 bg-white">
          <p className="font-size-14px text-[#c2c1c1] font-Poppins-Regular">
            Payment Method
          </p>
          <p className="font-size-16px text-[#8b8a8a] font-Poppins-Regular">
            {item?.paymentMethod || "Credit Card"}
          </p>
        </div>
      </div>
      {/* <div className="w-full flex 2xl:gap-10 gap-7 items-center justify-between">
        {isPharma && (
          <div className="w-[50%] flex flex-col gap-1 border border-gray-300 rounded-md 2xl:p-4 p-3 bg-white">
            <p className="font-size-14px text-[#c2c1c1] font-Poppins-Regular">
              Shipping Method
            </p>
            <p className="font-size-16px text-[#8b8a8a] font-Poppins-Regular">
              {item?.deliveryType === "instant delivery"
                ? "Instant Delivery"
                : "Standard Delivery"}
            </p>
          </div>
        )}
        <div className="w-[50%] flex flex-col gap-1 border border-gray-300 rounded-md 2xl:p-4 p-3 bg-white">
          <p className="font-size-14px text-[#c2c1c1] font-Poppins-Regular">
            Payment Method
          </p>
          <p className="font-size-16px text-[#8b8a8a] font-Poppins-Regular">
            {item?.paymentMethod || "Credit Card"}
          </p>
        </div>
      </div> */}
      <div style={{ background: "white", marginTop: "1rem" }}>
        <div className="total-table">
          <p
            style={{ color: "black", display: "flex", justifyContent: "end" }}
            className="font-size-15px font-Poppins-SemiBold"
          >
            Sub Total
          </p>
          <p
            style={{ textAlign: "center" }}
            className="font-size-15px font-Poppins-Regular "
          >{`${CURRENCY}  
                        ${Total.toFixed(2) || "-"}`}</p>
        </div>
        {isPharma && (
          <div className="total-table">
            <p
              style={{ color: "black", display: "flex", justifyContent: "end" }}
              className="font-size-15px font-Poppins-SemiBold"
            >
              Flat shipping Rate
            </p>
            <p
              style={{ textAlign: "center" }}
              className="font-size-15px font-Poppins-Regular "
            >{`${CURRENCY} ${(item?.deliveryType === "instant delivery"
              ? EXPRESS_DELIVERY_CHARGE
              : STANDARD_DELIVERY_CHARGE
            ).toFixed(2)}`}</p>
          </div>
        )}
        {/* <div className="total-table">
          <p
            style={{ color: "black", display: "flex", justifyContent: "end" }}
            className="font-size-15px font-Poppins-SemiBold"
          >
            Discount
          </p>
          <p
            style={{ textAlign: "center" }}
            className="font-size-15px font-Poppins-Regular "
          >{`${CURRENCY} ${(item?.discountedPrice || 0).toFixed(2)}`}</p>
        </div> */}
        <div className="total-table">
          <p
            style={{ color: "black", display: "flex", justifyContent: "end" }}
            className="font-size-15px font-Poppins-SemiBold"
          >
            Total
          </p>
          {/* <p style={{ textAlign: 'center' }} className="font-size-15px font-Poppins-Regular ">{`${CURRENCY} ${item?.discountedPrice || item?.totalAmount.toFixed(2)}`}</p> */}
          {/* <p style={{ textAlign: 'center' }} className="font-size-15px font-Poppins-Regular ">{`${CURRENCY} ${item?.discountedPrice ? item?.discountedPrice.toFixed(2) : item?.totalAmount.toFixed(2)}`}</p> */}
          <p
            style={{ textAlign: "center" }}
            className="font-size-15px font-Poppins-Regular "
          >{`${CURRENCY} ${item?.totalAmount.toFixed(2)}`}</p>
        </div>
      </div>
      <div
        style={{
          alignItems: "center",
          display: "flex",
          gap: "0.5rem",
          marginTop: "1rem",
        }}
        className="order-no-cust"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="19.44"
          height="19.44"
          viewBox="0 0 19.44 19.44"
        >
          <path
            id="Path_20131"
            data-name="Path 20131"
            d="M645.354-224.951a4.39,4.39,0,0,0-.1-.984,4.476,4.476,0,0,0-4.454-3.547h-5.142q-2.571,0-5.142,0a4.632,4.632,0,0,0-.786.055,4.5,4.5,0,0,0-3.8,4.455c-.016,2.105-.01,4.211-.007,6.316a4.075,4.075,0,0,0,.429,1.836,4.413,4.413,0,0,0,3.524,2.529c.177.025.226.083.223.26-.011.675,0,1.35,0,2.024,0,.533,0,1.066,0,1.6a.329.329,0,0,0,.4.354.566.566,0,0,0,.242-.176q1.909-1.913,3.815-3.83a.538.538,0,0,1,.424-.172q2.885.008,5.77,0a5.073,5.073,0,0,0,.687-.038,4.494,4.494,0,0,0,3.92-4.488C645.359-220.821,645.354-222.886,645.354-224.951Z"
            transform="translate(-625.916 229.483)"
            fill="#4f6367"
          />
        </svg>

        <p className="order-no font-size-20px font-Poppins-Regular">History</p>
      </div>
      <div
        style={{ color: "white" }}
        className="order-add-table order-add-table-second font-size-15px font-Poppins-SemiBold"
      >
        <p>Date Added</p>
        <p>Comments</p>
        <p>Status</p>
      </div>
      {history?.length ? (
        history.map((v) => (
          <div
            style={{ alignItems: "center" }}
            key={`${v?._id}-${history.length}`}
            className="sub-order-add-table sub-order-add-table-second font-size-15px font-Poppins-Regular"
          >
            <p>{moment(v?.dateAdded).format("DD-MM-YYYY")}</p>
            <p title={v?.comment}>{truncateString(v?.comment, 50) || "-"}</p>
            <p>{displayStatus(v?.status)?.text || ""}</p>
          </div>
        ))
      ) : (
        <div
          style={{ alignItems: "center" }}
          className="font-size-15px font-Poppins-Regular"
        >
          <p
            style={{
              color: "#646464",
              border: "1px solid #949494",
              textAlign: "center",
              padding: "0.5rem",
            }}
          >
            No history available
          </p>
        </div>
      )}
      {fileView && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[999]"
          onClick={handleCloseModal}
        >
          <div
            className="Prescription-modal-voice"
            onClick={(e) => e.stopPropagation()} // Prevent close when clicking inside the modal
          >
            <button onClick={handleCloseModal} style={backBtnMaain}>
              <FontAwesomeIcon
                className="font-size-16px "
                icon={faXmark}
                size="2x"
                style={backBtn}
              />
            </button>
            <img src={`${fileView}`} alt="Prescription" className="" />
          </div>
        </div>
      )}
      <Modal
        open={reviewModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <div
          style={customeStyle}
          className="flex items-center w-[35vw] flex-col gap-2 bg-[#FFFCF1] py-[20px] px-[40px] rounded-[8px]"
        >
          <button onClick={handleCloseReviewModal} style={backBtnMaain}>
            <FontAwesomeIcon
              className="font-size-16px text-white"
              icon={faXmark}
              size="2x"
            />
          </button>
          <h2 className="font-Poppins-SemiBold font-size-35px text-[#464646]">
            Write review
          </h2>
          <form onSubmit={handleSubmit}>
            <div>
              <div className="flex gap-4 my-4 items-start">
                <div className="w-full md:w-3/12">
                  <img
                    alt="profile-image"
                    className="w-[15vw] rounded-md"
                    // style={{ borderRadius: "4px" }}
                    src={`${
                      productDetails?.image ||
                      productDetails?.productsDetails?.image
                    }`}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      if (target) {
                        target.src = defaultProfile;
                      }
                    }}
                  />
                </div>
                <div className="w-full md:w-9/12">
                  <h2 className="font-Poppins-Medium font-size-18px  capitalize">
                    {productDetails?.productName}
                  </h2>

                  <div className="text-yellow-400 flex flex-row mt-2 reviewstars">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <FontAwesomeIcon
                        className={
                          star >= item ? "text-[#facc15]" : `text-[#b7b6b1]`
                        }
                        key={item}
                        role="button"
                        onClick={() => setStar(item)}
                        fontSize="20px"
                        icon={faStar}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <SimpleInput
                  isRequired={true}
                  placeholder="What did you think about?"
                  value={comment}
                  onChange={(val) => setComment(val)}
                  type="textArea"
                  rows={5}
                />
              </div>
            </div>
            <div className="flex flex-row cancel-job justify-center gap-2 mt-4">
              <PrimaryBtn
                onPress={handleCloseReviewModal}
                ButtonText="Cancel"
                btnExtraStyle={{
                  backgroundColor: "#FFFCF1",
                  borderRadius: "1.5rem",
                  color: "#00443f",
                  border: "1px solid #00443f",
                  borderColor: "#00443f",
                }}
              />
              <PrimaryBtn
                type="submit"
                loader={isLoader}
                ButtonText="Submit"
                btnExtraStyle={{
                  backgroundColor: "#00443f",
                  borderRadius: "1.5rem",
                }}
              />
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default DetailView;
